import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import AOS from "aos";
import "aos/dist/aos.css";

export const PrivacyPolicy = () => {
  useEffect(() => {
    AOS.init({
      duration: 900,
      easing: "ease-out-cubic",
      once: false,
    });
  }, []);

  const colors = {
    background: "#ffffff",
    darkBackground: "#f1f1f1",
    textPrimary: "#1c1c1c",
    textSecondary: "#6b6b6b",
    accent: "#1c1c1c",
  };

  const containerStyle = {
    padding: "64px 16px",
    backgroundColor: colors.background,
    fontFamily: "Inter, system-ui, sans-serif",
  };

  const sectionStyle = (isDark) => ({
    backgroundColor: isDark ? colors.darkBackground : "#ffffff",
    borderRadius: "10px",
    padding: "48px 32px",
    marginBottom: "24px",
    boxShadow: "0px 2px 10px rgba(28,28,28,0.05)",
  });

  const headingStyle = {
    textAlign: "center",
    fontSize: "2.4rem",
    fontWeight: 700,
    color: colors.textPrimary,
    marginBottom: "32px",
  };

  const titleStyle = {
    fontSize: "1.3rem",
    fontWeight: 600,
    color: colors.textPrimary,
    marginBottom: "12px",
  };

  const textStyle = {
    color: colors.textSecondary,
    lineHeight: 1.7,
    fontSize: "1rem",
  };

  // const listStyle = {
  //   paddingLeft: "20px",
  //   marginBottom: "16px",
  // };

  return (
    <>
      <Helmet>
        <title>Privacy Policy | Biblivium</title>
        <meta
          name="description"
          content="Learn how Biblivium collects, uses, and protects your information. Your privacy matters."
        />
      </Helmet>

      <main style={containerStyle}>
        <div data-aos="zoom-in" data-aos-delay="100">
          <h1 style={headingStyle}>Privacy Policy</h1>

          <div style={sectionStyle(false)}>
            <p style={textStyle}>
              At <strong>Biblivium</strong>, we value privacy as an essential part of trust. This Privacy Policy explains how we collect, use, store, and protect information when you visit our website or interact with our services. Our goal is clarity and transparency. We believe privacy should be understandable, respectful, and aligned with the calm, intentional experience that defines Biblivium.</p>
            <p style={textStyle}>
              We believe privacy should be understandable, not buried in jargon.
            </p>
          </div>

          <div style={sectionStyle(true)}>
            <h2 style={titleStyle}>1. Information We Collect</h2>
            <p style={textStyle}>
              We collect information that you choose to provide to us directly, such as your name, email address, and any details included in messages or inquiries submitted through our contact forms. This information is provided voluntarily and is used only for communication and support purposes.</p>
              <p style={textStyle}>
                We also collect certain information automatically when you access the website. This may include your IP address, browser type, device information, pages visited, time spent on the site, and referring URLs. This data helps us understand how visitors engage with Biblivium and allows us to improve performance and usability.
              </p>
          </div>

          <div style={sectionStyle(false)}>
            <h2 style={titleStyle}>2. How We Use Your Information</h2>
            <p style={textStyle}>
              We use collected information to respond to inquiries, provide assistance, and maintain communication when requested. Information may also be used to improve the quality, structure, and clarity of our content, as well as to enhance the overall reader experience.</p>
            <p style={textStyle}>
              Additionally, we analyze general usage patterns to understand how visitors navigate the site and to ensure stability, security, and functionality. We do not sell, rent, or share personal information with third parties for advertising or marketing purposes.</p>
          </div>

          <div style={sectionStyle(true)}>
            <h2 style={titleStyle}>3. Third-Party Services</h2>
            <p style={textStyle}>
              To operate effectively, Biblivium may rely on trusted third-party service providers, such as email platforms, hosting providers, or analytics services. These providers process information solely to support the operation and improvement of the website.</p>
            <p style={textStyle}>
              We work with services that are committed to safeguarding data and using it only in accordance with contractual obligations and applicable privacy standards. We do not authorize third parties to use your information for unrelated or promotional purposes.</p>
          </div>

          <div style={sectionStyle(false)}>
            <h2 style={titleStyle}>4. Cookies and Tracking</h2>
            <p style={textStyle}>
              Biblivium uses cookies and similar technologies to enhance site functionality and better understand how visitors interact with the platform. Cookies allow us to maintain preferences, improve navigation, and analyze performance trends.</p>
            <p style={textStyle}>
              You may manage, restrict, or disable cookies through your browser settings at any time. Please note that disabling certain cookies may affect how some parts of the website function.</p>
          </div>

          <div style={sectionStyle(true)}>
            <h2 style={titleStyle}>5. Data Security</h2>
            <p style={textStyle}>
              We implement appropriate technical and organizational measures designed to protect personal information against unauthorized access, alteration, disclosure, or destruction. These measures are regularly reviewed to maintain a secure digital environment.</p>
            <p style={textStyle}>
              While we take data protection seriously, no online platform can guarantee absolute security. We encourage users to exercise reasonable care when sharing information online.</p>
          </div>

          <div style={sectionStyle(false)}>
            <h2 style={titleStyle}>6. Your Rights</h2>
            <p style={textStyle}>
              Depending on your jurisdiction, you may have the right to request access to your personal data, to correct inaccurate information, to request deletion, or to withdraw consent where processing is based on consent. You may also have the right to object to certain types of data processing.</p>
            <p style={textStyle}>
              Requests regarding personal data may be submitted to <a href="mailto:info@lonewolfwrites.com" style={{ color: colors.accent }}>info@lonewolfwrites.com</a>. We will respond in accordance with applicable laws and within a reasonable timeframe.</p>
          </div>

          <div style={sectionStyle(true)}>
            <h2 style={titleStyle}>7. Changes to This Policy</h2>
            <p style={textStyle}>
              We may update this Privacy Policy periodically to reflect operational, legal, or regulatory changes. When updates are made, the revised version will be published on this page with an updated effective date. Continued use of the website after changes are posted constitutes acceptance of the revised policy.</p>
          </div>

          <div style={sectionStyle(false)}>
            <h2 style={titleStyle}>8. Contact Us</h2>
            <p style={textStyle}>
              If you have questions or concerns about this Privacy Policy or how your information is handled, you may contact us at <a href="mailto:info@lonewolfwrites.com" style={{ color: colors.accent }}>info@lonewolfwrites.com</a>. We are committed to addressing inquiries thoughtfully and transparently</p>
          </div>
        </div>
      </main>
    </>
  );
};
